<template>
  <div>
    <page-header :title="title" :container-class="containerClass" info-text="List of research opportunities posted by you." />
    <div class="page-section">
      <div :class="containerClass">
        <page-separator title="" />

        <div class="d-flex justify-content-center" v-if="!totalResearch && !isLoading && !searchTerm">
          <div class="card card-block card-stretch card-height col-md-8">
            <div class="card-body text-center">
              <i class="fas fa-exclamation-circle fa-lg text-muted mt-4 mb-4" style="font-size: 3rem" />
              <h4>No Research Posted</h4>
              <p class="text-muted">
                You haven't posted any research opportunity yet. Click below to post one and let candidates know about it.
              </p>
              <router-link :to="{ name: 'post-research' }" class="btn btn-primary">Post Research</router-link>
            </div>
          </div>
        </div>

        <div class="card mb-0" v-else>
          <div class="card-header">
            <div class="row align-items-center" style="white-space: nowrap">
              <div class="col-lg-auto">
                <form class="search-form search-form--light d-lg-inline-flex mb-8pt mb-lg-0" @submit.prevent="">
                  <b-form-input
                    class="w-lg-auto"
                    placeholder="Search Research"
                    v-model="searchTerm"
                    @input="onSearch"
                    :disabled="isLoading"
                  />
                  <b-btn variant="flush" type="submit">
                    <md-icon v-text="'search'" />
                  </b-btn>
                </form>
              </div>
              <div class="col-lg d-flex flex-wrap justify-content-end">
                <b-btn :to="{ name: 'post-research' }" exact variant="primary">Post Research</b-btn>
              </div>
            </div>
          </div>

          <b-skeleton-wrapper :loading="isLoading">
            <template #loading>
              <b-skeleton-table :rows="5" :columns="4" :table-props="{ hover: true }"></b-skeleton-table>
            </template>

            <b-table
              :fields="tableColumns"
              :items="research"
              :busy="isLoading"
              head-variant="light"
              class="table-nowrap"
              hover
              responsive
              no-local-sorting
              @sort-changed="onSortChange"
              @row-clicked="editResearch"
            >
              <template #cell(menu)="data">
                <div class="text-center">
                  <b-dropdown :boundary="'main'" no-flip variant="flush" toggle-class="text-muted" no-caret>
                    <template #button-content>
                      <i class="material-icons icon-24pt card-course__icon-favorite text-info">more_vert</i>
                      <span class="sr-only">Menu</span>
                    </template>
                    <b-dropdown-item
                      v-if="canManage(data.item)"
                      href="#"
                      @click.prevent="toggleStatus(data.index)"
                      :title="data.item.is_active ? 'Mark Inactive' : 'Mark Active'"
                    >
                      <md-icon :class="data.item.is_active ? 'text-danger' : 'text-success'">{{
                        data.item.is_active ? 'block' : 'check_circle_outline'
                      }}</md-icon>
                      {{ data.item.is_active ? 'Mark Inactive' : 'Mark Active' }}
                    </b-dropdown-item>
                    <b-dropdown-item v-if="canManage(data.item)" :to="{ name: 'edit-research', params: { id: data.item.id } }">
                      <md-icon class="text-info">edit</md-icon>
                      Edit
                    </b-dropdown-item>
                    <b-dropdown-item v-if="canManage(data.item)" href="#" @click.prevent="confirmDelete(data.item.id)">
                      <md-icon class="text-danger">delete</md-icon>
                      Delete
                    </b-dropdown-item>
                    <b-dropdown-item v-else disabled>
                      <md-icon class="text-muted">lock</md-icon>
                      Posted by admin
                    </b-dropdown-item>
                  </b-dropdown>
                </div>
              </template>

              <template #cell(title)="data">
                <strong>{{ data.item.title }}</strong>
              </template>

              <template #cell(universities)="data">
                {{ universityNames(data.item) }}
              </template>

              <template #cell(address)="data">
                {{ data.item.city }}, {{ data.item.state }}
              </template>

              <template #cell(status)="data">
                <b-badge
                  :class="
                    'border border-' +
                    (data.item.is_expired
                      ? `secondary text-secondary`
                      : data.item.is_active
                      ? `success text-success`
                      : `danger text-danger`)
                  "
                  variant="none"
                  >{{ data.item.is_expired ? 'Expired' : data.item.is_active ? 'Active' : 'Inactive' }}</b-badge
                >
              </template>
            </b-table>

            <div class="card-footer">
              <pagination
                v-model="currentPage"
                :total-rows="totalResearch"
                :per-page="perPage"
                @change="onPageChange"
                aria-controls="research-table"
              />
            </div>
          </b-skeleton-wrapper>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PageSeparator from '@/components/Ui/PageSeparator.vue';
import { debounce, get } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import moment from 'moment';
import PageHeader from '@/components/Ui/PageHeader.vue';
import Page from '@/components/Page.vue';
import Pagination from '../../components/Ui/Pagination.vue';
import MdIcon from '../../components/Ui/MdIcon.vue';
import { DEFAULT_PAGE_SIZE, USER_ROLES, USER_ROLE_TYPES } from '../../common/constants';

export default {
  components: { MdIcon, PageHeader, PageSeparator, Pagination },
  extends: Page,
  name: 'Research',

  data() {
    return {
      title: 'Research',
      isLoading: true,
      perPage: DEFAULT_PAGE_SIZE,
      currentPage: 1,
      totalResearch: 0,
      research: [],
      ordering: '-is_active,-id',
      USER_ROLE_TYPES,
      USER_ROLES,
      searchTerm: '',
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        { text: 'Research', active: true },
      ];
    },
    tableColumns() {
      return [
        { key: 'menu', label: '', tdClass: 'text-left' },
        { key: 'id', label: 'Research ID', tdClass: 'clickable-item' },
        { key: 'title', label: 'Title', tdClass: 'clickable-item' },
        { key: 'universities', label: 'Universities', tdClass: 'clickable-item' },
        { key: 'address', label: 'Location', tdClass: 'clickable-item' },
        { key: 'status', label: this.$t('generalMsgs.status'), tdClass: 'clickable-item' },
        {
          key: 'views_count',
          label: 'Clicks',
          formatter: (value) => this.$n(value || 0),
          tdClass: 'clickable-item',
        },
        {
          key: 'created_at',
          label: 'Posted On',
          formatter: (value) => moment(value).format('HH:mm, MM/DD/YYYY'),
          tdClass: 'clickable-item',
        },
      ];
    },
  },

  methods: {
    ...mapActions('research', ['getAllResearch', 'updateResearch', 'deleteResearch']),
    get,
    universityNames(item) {
      return (get(item, 'universities', []) || []).map((uni) => uni.name).join(', ') || '-';
    },

    // Super admins manage everything; organizations can only manage listings they posted.
    canManage(item) {
      if (this.getLoggedInUser.role === USER_ROLES.SUPER_ADMIN) {
        return true;
      }
      return get(item, 'ext_posted_by.id') === get(this.getLoggedInUser, 'linked_entity.id');
    },

    async fetchResearch(pageNum = 1, params = {}) {
      this.isLoading = true;
      document.getElementById('app').scrollIntoView();
      const response = await this.getAllResearch({
        limit: this.perPage,
        offset: (pageNum - 1) * this.perPage,
        ...(this.ordering && { ordering: this.ordering }),
        ...(this.searchTerm && { search: this.searchTerm }),
        ...params,
      });
      this.research = response.data.results;
      this.currentPage = pageNum;
      this.totalResearch = response.data.count;
      this.isLoading = false;
    },

    onPageChange(pageNum) {
      this.fetchResearch(pageNum);
    },

    onSortChange(context) {
      this.ordering = context.sortDesc ? '-' + context.sortBy : context.sortBy;
      this.fetchResearch();
    },

    onSearch() {
      this.debouncedSearch(this);
    },

    debouncedSearch: debounce((vm) => {
      vm.fetchResearch();
    }, 500),

    async toggleStatus(index) {
      const currStatus = this.research[index].is_active;
      this.research[index].is_active = !currStatus;
      await this.updateResearch({ id: this.research[index].id, data: { is_active: !currStatus } });
      this.makeToast({ variant: 'success', msg: `Research marked as ${currStatus ? 'Inactive' : 'Active'}` });
    },

    async confirmDelete(id) {
      try {
        const isConfirmed = await this.$bvModal.msgBoxConfirm(
          `Please confirm that you want to remove the selected research opportunity. You will not be able to access any related data after removal.`,
          {
            title: 'Are you sure?',
            size: 'md',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: 'Yes',
            cancelTitle: 'No',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true,
          }
        );
        if (isConfirmed) {
          await this.deleteResearch(id);
          const refreshPage = this.research.length > 1 ? this.currentPage : this.currentPage - 1;
          this.fetchResearch(refreshPage || 1);
          this.makeToast({ variant: 'success', msg: 'Research Successfully Removed' });
        }
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },

    editResearch(row) {
      if (!this.canManage(row)) {
        this.makeToast({ variant: 'warning', msg: "You can't edit a research listing posted by an admin." });
        return;
      }
      this.$router.push({ name: 'edit-research', params: { id: row.id } });
    },
  },

  async mounted() {
    this.fetchResearch();
  },
};
</script>
